import { makeObservable, observable, action, runInAction } from 'mobx';
import { timer, Subscription } from 'rxjs';
import { IStoredDevice } from '@store/Devices.store';
import { ICoords, IRawTelemetry, ITelemetry } from '@core/Telemetry';
import { RootStore } from './Root.store';

export type position = [number, number];

export interface IHistoryTelemetry
    extends Pick<ITelemetry, 'cur_sim' | 'temperature' | 'event'>,
        Pick<
            IRawTelemetry,
            'speed' | 'alt' | 'dir' | 'sats' | 'hdop' | 'signal'
        >,
        Pick<IStoredDevice, 'imei' | 'name' | 'icon'> {
    date: Date | null;
    batteryVoltage: number;
    gpsDate: Date | null;
    lbsDate: Date | null;
    gpsCoord: ICoords;
    lbsCoord: ICoords;
    address: string;
}

export default class MapStore {
    @observable
    public defaultPosition: position = [55.013872, 82.954099];

    @observable
    public fromDateFilter: Date;

    @observable
    public toDateFilter: Date;

    @observable
    public reload: boolean;

    @observable
    public position: position;

    @observable
    public currentPosition: position;

    @observable
    public zoom: number;

    @observable
    public selectedGroupId: string | null;

    @observable
    public selectedGroupDevices: string[];

    @observable
    public selectedDevices: string[];

    @observable
    public selectedDevice: string | null;

    @observable
    public highlightedCoords: string | null;

    @observable
    public highlightedDate: string | null;

    @observable
    public highlightedDevice: string | null;

    @observable
    public highlightTimer: Subscription | null;

    @observable
    public lastCoordsList: IStoredDevice[];

    @observable
    public historyCoordsList: IHistoryTelemetry[];

    @observable
    public loading: boolean;

    @observable
    public serverError: boolean;

    @observable
    public dateFilter: string;

    @observable
    public trackMode: boolean;

    @observable
    public isBounds: boolean;

    constructor(private rootStore: RootStore) {
        this.setDefault();
        makeObservable(this);
    }

    @action
    setDefault() {
        this.dateFilter = 'last24Hours';
        this.fromDateFilter = new Date(Date.now() - 24 * 60 * 60 * 1000);
        this.toDateFilter = new Date(Date.now());
        this.position = this.defaultPosition;
        this.currentPosition = this.defaultPosition;
        this.zoom = 15;
        this.selectedGroupId = null;
        this.selectedDevices = [];
        this.selectedGroupDevices = [];
        this.lastCoordsList = [];
        this.historyCoordsList = [];
        this.selectedDevice = null;
        this.highlightedDevice = null;
        this.loading = false;
        this.serverError = false;
        this.reload = false;
        this.highlightTimer = null;
        this.highlightedCoords = null;
        this.highlightedDate = null;
        this.trackMode = false;
        this.isBounds = true;
    }

    @action
    startHighlightTimer() {
        if (this.highlightTimer) {
            this.highlightTimer.unsubscribe();
        }
        const timeout = 3500;
        const stream = timer(timeout);
        this.highlightTimer = stream.subscribe({
            next: () => {
                runInAction(() => {
                    this.highlightedDevice = null;
                    this.highlightedCoords = null;
                    this.highlightedDate = null;
                    this.reload = true;
                });
            },
        });
    }

    @action
    stopHighlightTimer() {
        if (this.highlightTimer) {
            this.highlightTimer.unsubscribe();
        }
    }

    @action
    setDateFilter(filter: string) {
        this.dateFilter = filter;
    }

    @action
    setFromDateFilter(date: Date) {
        this.fromDateFilter = date;
    }

    @action
    setToDateFilter(date: Date) {
        this.toDateFilter = date;
    }

    @action
    resetDataFilters() {
        this.fromDateFilter = new Date(Date.now() - 24 * 60 * 60 * 1000);
        this.toDateFilter = new Date(Date.now());
    }

    @action
    setZoom(zoom: number) {
        this.zoom = zoom;
    }

    @action
    setPosition(position: ICoords) {
        if (position) {
            const { lat, lon } = position;
            const isLat = typeof lat === 'number';
            const isLon = typeof lon === 'number';
            if (isLat && isLon) {
                this.position = [lat, lon];
            }
        }
    }

    @action
    resetPosition() {
        this.position = [0, 0];
    }

    @action
    setSelectedGroupId(id: string) {
        this.selectedGroupId = id;
    }

    @action
    resetSelectedGroupId() {
        this.selectedGroupId = null;
        if (
            this.rootStore.app.page !== 'history' &&
            this.rootStore.app.page !== 'support'
        ) {
            this.selectedDevice = null;
        }
    }

    @action
    setReload() {
        this.reload = true;
    }

    @action
    resetReload() {
        this.reload = false;
    }

    @action
    setSelectedDevices(groupId: string | null) {
        if (groupId) {
            const devices = this.rootStore.devices.deviceList
                .filter((device) => device.groupId === groupId)
                .map(({ imei }) => imei);
            this.selectedGroupDevices = [...devices];
            if (
                !this.selectedDevice ||
                this.rootStore.app.page === 'history' ||
                this.rootStore.app.page === 'support'
            ) {
                this.selectedDevices = [...devices];
            }
        } else {
            this.selectedDevices = [];
            this.selectedGroupDevices = [];
        }
    }

    @action
    removeSelectedDevices(imei: string) {
        this.selectedDevices = this.selectedDevices.filter(
            (item) => item !== imei,
        );
        this.removeLastCoordsList(imei);
        this.selectedDevice = null;
    }

    @action
    resetSelectedDevices() {
        this.selectedDevices = [];
    }

    @action
    addSelectedDevices(imei: string) {
        if (
            this.selectedGroupDevices.indexOf(imei) > -1 &&
            this.selectedDevices.indexOf(imei) === -1 &&
            this.rootStore.app.page !== 'history' &&
            this.rootStore.app.page !== 'support'
        ) {
            this.selectedDevices = [...this.selectedDevices, imei];
            this.addLastCoordsList(imei);
            this.selectedDevice = null;
        } else if (
            !this.selectedGroupId &&
            this.rootStore.app.page !== 'history' &&
            this.rootStore.app.page !== 'support'
        ) {
            const [device] = this.rootStore.devices.deviceList.filter(
                (device) => device.imei === imei,
            );
            if (device) {
                this.selectedDevices = [imei];
                this.setSelectedGroupId(device.groupId);
            }
        }
    }

    @action
    setLastCoordsList(lastCoordsList: IStoredDevice[]) {
        this.lastCoordsList = lastCoordsList.filter(
            ({ imei }) => this.selectedDevices.indexOf(imei) > -1,
        );

        if (this.rootStore.app.page === 'support') {
            this.lastCoordsList = lastCoordsList;
        }
    }

    @action
    resetLastCoordsList() {
        this.lastCoordsList = [];
    }

    @action
    addLastCoordsList(imei: string) {
        const allCoords = this.rootStore.devices.lastCoordsList;
        const [coords] = allCoords.filter((item) => item.imei === imei);
        if (coords) {
            this.lastCoordsList = [...this.lastCoordsList, coords];
        }
    }

    @action
    removeLastCoordsList(imei: string) {
        this.lastCoordsList = this.lastCoordsList.filter(
            (item) => item.imei !== imei,
        );
    }

    @action
    setCurrentPosition(position: position) {
        this.currentPosition = position;
    }

    @action
    setSelectedDevice(imei: string) {
        this.selectedDevice = imei;
    }

    @action
    resetSelectedDevice() {
        this.selectedDevice = null;
    }

    @action
    getHistoryCoordsList(imei: string) {
        const deviceList = this.rootStore.devices.deviceList;
        const supportList = this.rootStore.devices.supportList;
        if (supportList.length || deviceList.length) {
            let name = imei;
            let icon = 1;
            if (!supportList.length) {
                const [device] = deviceList.filter((d) => d.imei === imei);
                name = device.name;
                icon = device.icon;
            }
            const getCurSim = (code: number) => {
                switch (code) {
                    case 1:
                        return 'SIM 1';
                    case 2:
                        return 'SIM 2';
                    default:
                        return '';
                }
            };
            this.historyCoordsList = this.rootStore.telemetry.telemetryList
                .map(
                    ({
                        lat,
                        lon,
                        lat_lbs,
                        lon_lbs,
                        ts_s,
                        ts_d,
                        speed,
                        dir,
                        alt,
                        sats,
                        hdop,
                        address,
                        bat_v: batteryVoltage,
                        event,
                        temp,
                        cur_sim,
                        signal,
                    }) => {
                        const gpsCoord = { lat, lon };
                        const lbsCoord = { lat: lat_lbs, lon: lon_lbs };
                        const gpsDate =
                            lat && lon
                                ? ts_d
                                    ? new Date(ts_d)
                                    : new Date(ts_s)
                                : null;
                        const lbsDate =
                            lat_lbs && lon_lbs
                                ? ts_d
                                    ? new Date(ts_d)
                                    : new Date(ts_s)
                                : null;
                        const isTelemetry =
                            speed ||
                            dir ||
                            alt ||
                            sats ||
                            hdop ||
                            temp ||
                            batteryVoltage;
                        const date = isTelemetry
                            ? ts_d
                                ? new Date(ts_d)
                                : new Date(ts_s)
                            : null;
                        return {
                            imei,
                            name,
                            icon,
                            gpsCoord,
                            lbsCoord,
                            date,
                            gpsDate,
                            lbsDate,
                            speed,
                            dir,
                            alt,
                            sats,
                            hdop,
                            batteryVoltage,
                            address,
                            event:
                                typeof event === 'string'
                                    ? event.toLowerCase()
                                    : '',
                            temperature: temp,
                            cur_sim: getCurSim(cur_sim),
                            signal,
                        };
                    },
                )
                .filter(({ gpsCoord, lbsCoord, gpsDate, lbsDate }) => {
                    let date: Date | null = null;
                    const isGps = !!(gpsCoord && gpsCoord.lat && gpsCoord.lon);
                    const isLbs = !!(lbsCoord && lbsCoord.lat && lbsCoord.lon);
                    if (!isGps && !isLbs) return false;
                    if (isGps) date = gpsDate;
                    else if (isLbs) date = lbsDate;
                    const dateMin = Math.floor(
                        (date ? date.getTime() : Date.now()) / 1000 / 60,
                    );
                    const fromMin = Math.floor(
                        this.fromDateFilter.getTime() / 1000 / 60,
                    );
                    const toMin = Math.floor(
                        this.toDateFilter.getTime() / 1000 / 60,
                    );
                    const isEarlierThanFrom = dateMin < fromMin;
                    const isLaterThanTo = dateMin > toMin;
                    return !(isEarlierThanFrom || isLaterThanTo);
                });
        }
    }

    @action
    async updateHistoryCoordsList() {
        const imei = this.rootStore.telemetry.selectedDevice;
        const deviceList = this.rootStore.devices.deviceList;
        this.switchDate(deviceList);
        await this.rootStore.telemetry.getTelemetry();
        imei && this.getHistoryCoordsList(imei);
    }

    @action
    switchDate(deviceList: IStoredDevice[]) {
        const selectedGroupId = this.selectedGroupId;
        const selectedGroup = deviceList
            .filter((device) => device.groupId === selectedGroupId)
            .map((device) => device.imei);
        this.selectedGroupDevices = [...selectedGroup];
        this.selectedDevices = this.selectedDevices.filter(
            (imei) => selectedGroup.indexOf(imei) > -1,
        );
        switch (this.dateFilter) {
            case 'last24Hours':
                this.setFromDateFilter(
                    new Date(Date.now() - 24 * 60 * 60 * 1000),
                );
                this.setToDateFilter(new Date(Date.now()));
                break;
            case 'last3Days':
                this.setFromDateFilter(
                    new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
                );
                this.rootStore.map.setToDateFilter(new Date(Date.now()));
                break;
            case 'lastWeek':
                this.setFromDateFilter(
                    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                );
                this.setToDateFilter(new Date(Date.now()));
                break;
        }
        const subSelectedDevice = this.rootStore.subusers.selectedDevice;
        if (
            subSelectedDevice &&
            !this.rootStore.devices.loading &&
            deviceList.some(({ imei }) => imei === subSelectedDevice)
        ) {
            this.rootStore.subusers.resetSelectedDevice();
        }
    }

    @action
    resetHistoryCoordsList() {
        this.historyCoordsList = [];
    }

    @action
    setHighlightedCoords(coords: string, date: string) {
        this.highlightedCoords = coords;
        this.highlightedDate = date;
    }

    @action
    resetHighlightedCoords() {
        this.highlightedCoords = null;
        this.highlightedDate = null;
    }

    @action
    setHighlightedDevice(imei: string) {
        this.highlightedDevice = imei;
    }

    @action
    resetHighlightedDevice() {
        this.highlightedDevice = null;
    }

    @action
    switchTrackMode() {
        this.trackMode = !this.trackMode;
    }

    @action
    setTrackMode() {
        this.trackMode = true;
    }

    @action
    resetTrackMode() {
        this.trackMode = false;
    }

    @action
    setBounds() {
        this.isBounds = true;
    }

    @action
    resetBounds() {
        this.isBounds = false;
    }
}
